// useDebounce.ts
import { useRef, useEffect, useCallback } from "react"
import debounce from "lodash/debounce"
import type { DebounceSettings } from "lodash"

type UseDebounceOptions = DebounceSettings & {
  flushOnUnmount?: boolean
}

/**
 * Returns a debounced version of a callback
 * @param callback function to debounce
 * @param delay debounce delay in ms
 */
export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  { flushOnUnmount = false, ...debounceOptions }: UseDebounceOptions = {}
) {
  const callbackRef = useRef(callback)

  // keep latest callback
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  // create debounced function
  const debouncedFn = useRef(
    debounce(
      (...args: Parameters<T>) => {
        callbackRef.current(...args)
      },
      delay,
      debounceOptions
    )
  ).current

  useEffect(() => {
    return () => {
      if (flushOnUnmount) debouncedFn.flush()
      else debouncedFn.cancel()
    }
  }, [debouncedFn, flushOnUnmount])

  const flush = useCallback(() => {
    debouncedFn.flush()
  }, [debouncedFn])

  return { debouncedFn, flush }
}
